import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { I } from '../../../../common/v5/config';
import { TextInputRow } from '../../../../reactcomponents/Form';
import { UnnestedAreaSingleSelect } from '../../../../reactcomponents/Dropdown';
import { setCurrentWorkflow, setWorkflowSettings } from '../../../../redux/actions/async/agentBuilder';

const SettingsContainer = styled.div`
  background: var(--cention-color-background-xlight);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SettingsHeader = styled.div`
  padding: var(--cention-spacing-m);
  border-bottom: var(--cention-border-width-base) solid var(--cention-color-border-base);
  background: var(--cention-color-background-xlight);
`;

const SettingsTitle = styled.h2`
  font-size: var(--cention-font-size-l);
  font-weight: var(--cention-font-weight-bold);
  color: var(--cention-color-text-dark);
  margin: 0 0 var(--cention-spacing-xs) 0;
`;

const SettingsSubtitle = styled.p`
  color: var(--cention-color-text-base);
  font-size: var(--cention-font-size-s);
  margin: 0;
`;

const SettingsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--cention-spacing-m);
  padding-bottom: 80px; /* Space for save button */
`;

const SaveButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--cention-color-background-xlight);
  border-top: var(--cention-border-width-base) solid var(--cention-color-border-base);
  padding: var(--cention-spacing-m);
  display: flex;
  justify-content: flex-end;
  gap: var(--cention-spacing-s);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const SaveButton = styled.button`
  background: var(--cention-color-primary);
  color: white;
  border: none;
  border-radius: var(--cention-border-radius-base);
  padding: var(--cention-spacing-s) var(--cention-spacing-l);
  font-size: var(--cention-font-size-s);
  font-weight: var(--cention-font-weight-semibold);
  cursor: pointer;
  transition: all 150ms ease-in-out;
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  min-height: 36px;

  &:hover:not(:disabled) {
    background: var(--cention-color-primary-shade-1);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(255, 109, 90, 0.3);
  }

  &:active {
    background: var(--cention-color-primary-shade-2);
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  i {
    font-size: 14px;
  }
`;

const ResetButton = styled.button`
  background: var(--cention-color-background-light);
  color: var(--cention-color-text-dark);
  border: var(--cention-border-width-base) solid var(--cention-color-border-base);
  border-radius: var(--cention-border-radius-base);
  padding: var(--cention-spacing-s) var(--cention-spacing-l);
  font-size: var(--cention-font-size-s);
  font-weight: var(--cention-font-weight-semibold);
  cursor: pointer;
  transition: all 150ms ease-in-out;
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  min-height: 36px;

  &:hover:not(:disabled) {
    background: var(--cention-color-background-base);
    border-color: var(--cention-color-border-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: var(--cention-color-background-dark);
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  i {
    font-size: 14px;
  }
`;

const SettingsSection = styled.div`
  margin-bottom: var(--cention-spacing-l);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: var(--cention-font-size-m);
  font-weight: var(--cention-font-weight-bold);
  color: var(--cention-color-text-dark);
  margin: 0 0 var(--cention-spacing-s) 0;
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
`;

const SectionIcon = styled.i`
  color: var(--cention-color-text-light);
  font-size: var(--cention-font-size-s);
`;

const FormGroup = styled.div`
  margin-bottom: var(--cention-spacing-m);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: var(--cention-font-size-s);
  font-weight: var(--cention-font-weight-medium);
  color: var(--cention-color-text-dark);
  margin-bottom: var(--cention-spacing-xs);
`;

const HelpText = styled.div`
  font-size: var(--cention-font-size-xs);
  color: var(--cention-color-text-light);
  margin-top: var(--cention-spacing-xs);
  line-height: 1.4;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  margin-top: var(--cention-spacing-xs);
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--cention-color-primary);
`;

const CheckboxLabel = styled.label`
  font-size: var(--cention-font-size-s);
  color: var(--cention-color-text-base);
  cursor: pointer;
  margin: 0;
`;

const TimeoutInput = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  margin-top: var(--cention-spacing-xs);
`;

const TimeoutUnit = styled.span`
  font-size: var(--cention-font-size-s);
  color: var(--cention-color-text-base);
`;

const WorkflowSettings = ({
  currentWorkflow,
  areaList = [],
  onWorkflowUpdate,
  onSaveWorkflow,
  onClose
}) => {
  const dispatch = useDispatch();
  const [settings, setSettings] = useState({
    timeout: 300,
    enableTimeout: false,
    saveExecutionData: true,
    saveManualExecutions: false,
    enableDebugMode: false,
    autoSave: true,
    retryOnFailure: false,
    maxRetries: 3,
    retryInterval: 60
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});
  const isUpdatingFromProps = useRef(false);

  useEffect(() => {
    if (currentWorkflow && !isUpdatingFromProps.current) {
      const newSettings = {
        timeout: parseInt(currentWorkflow.settings?.timeout, 10) || 300,
        enableTimeout: currentWorkflow.settings?.enableTimeout || false,
        saveExecutionData: currentWorkflow.settings?.saveExecutionData !== false,
        saveManualExecutions: currentWorkflow.settings?.saveManualExecutions || false,
        enableDebugMode: currentWorkflow.settings?.enableDebugMode || false,
        autoSave: currentWorkflow.settings?.autoSave !== false,
        retryOnFailure: currentWorkflow.settings?.retryOnFailure || false,
        maxRetries: parseInt(currentWorkflow.settings?.maxRetries, 10) || 3,
        retryInterval: parseInt(currentWorkflow.settings?.retryInterval, 10) || 60
      };
      

      setSettings(newSettings);
      setOriginalSettings({ ...newSettings });
      setHasChanges(false);
    }
    
    // Reset the flag after the effect runs
    if (isUpdatingFromProps.current) {
      isUpdatingFromProps.current = false;
    }
  }, [currentWorkflow]);

    const handleSettingChange = (key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      
      // Check if settings have changed
      const changed = JSON.stringify(newSettings) !== JSON.stringify(originalSettings);

      setHasChanges(changed);
      
      // Only update workflow settings in memory (for preview), don't save to Redux yet
      if (onWorkflowUpdate && currentWorkflow) {
        isUpdatingFromProps.current = true; // Prevent useEffect from resetting originalSettings
        const updatedWorkflow = {
          ...currentWorkflow,
          settings: {
            ...currentWorkflow.settings,
            timeout: newSettings.timeout,
            enableTimeout: newSettings.enableTimeout,
            saveExecutionData: newSettings.saveExecutionData,
            saveManualExecutions: newSettings.saveManualExecutions,
            enableDebugMode: newSettings.enableDebugMode,
            autoSave: newSettings.autoSave,
            retryOnFailure: newSettings.retryOnFailure,
            maxRetries: newSettings.maxRetries,
            retryInterval: newSettings.retryInterval
          }
        };
        onWorkflowUpdate(updatedWorkflow);
      }

      return newSettings;
    });
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;
    
    setIsSaving(true);
    try {
      // Update Redux state with settings
      dispatch(setWorkflowSettings({
        timeout: settings.timeout,
        enableTimeout: settings.enableTimeout,
        saveExecutionData: settings.saveExecutionData,
        saveManualExecutions: settings.saveManualExecutions,
        enableDebugMode: settings.enableDebugMode,
        autoSave: settings.autoSave,
        retryOnFailure: settings.retryOnFailure,
        maxRetries: settings.maxRetries,
        retryInterval: settings.retryInterval
      }));
      
      // Update current workflow with new settings
      if (currentWorkflow) {
        const updatedWorkflow = {
          ...currentWorkflow,
          settings: {
            ...currentWorkflow.settings,
            timeout: settings.timeout,
            enableTimeout: settings.enableTimeout,
            saveExecutionData: settings.saveExecutionData,
            saveManualExecutions: settings.saveManualExecutions,
            enableDebugMode: settings.enableDebugMode,
            autoSave: settings.autoSave,
            retryOnFailure: settings.retryOnFailure,
            maxRetries: settings.maxRetries,
            retryInterval: settings.retryInterval
          }
        };
        dispatch(setCurrentWorkflow(updatedWorkflow));
      }
      
      // Now save to backend (only workflow-level changes, settings are handled by Redux)
      if (onSaveWorkflow) {
        const result = await onSaveWorkflow();
        
        if (result) {
          setOriginalSettings(settings);
          setHasChanges(false);
        }
      }
    } catch (error) {
      console.error('Failed to save workflow:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    setHasChanges(false);
    
    // Reset the workflow settings in memory (for preview)
    if (onWorkflowUpdate && currentWorkflow) {
      isUpdatingFromProps.current = true; // Prevent useEffect from resetting originalSettings
      const resetWorkflow = {
        ...currentWorkflow,
        settings: {
          ...currentWorkflow.settings,
          timeout: originalSettings.timeout,
          enableTimeout: originalSettings.enableTimeout,
          saveExecutionData: originalSettings.saveExecutionData,
          saveManualExecutions: originalSettings.saveManualExecutions,
          enableDebugMode: originalSettings.enableDebugMode,
          autoSave: originalSettings.autoSave,
          retryOnFailure: originalSettings.retryOnFailure,
          maxRetries: originalSettings.maxRetries,
          retryInterval: originalSettings.retryInterval
        }
      };
      onWorkflowUpdate(resetWorkflow);
    }
  };

  const timeoutOptions = [
    { key: 'seconds', value: I('Seconds') },
    { key: 'minutes', value: I('Minutes') },
    { key: 'hours', value: I('Hours') }
  ];

  // Initialize a default workflow if none exists
  useEffect(() => {
    if (!currentWorkflow) {
      const defaultWorkflow = {
        workflowId: 'new',
        name: 'New Workflow',
        description: '',
        active: true,
        nodes: [],
        connections: [],
        settings: {
          timeout: 300,
          enableTimeout: false,
          saveExecutionData: true,
          saveManualExecutions: false,
          enableDebugMode: false,
          autoSave: true,
          retryOnFailure: false,
          maxRetries: 3,
          retryInterval: 60
        },
        tags: [],
        meta: {},
        systemGroup: 'default',
        area: 'default',
        spc: 'default'
      };
      dispatch(setCurrentWorkflow(defaultWorkflow));
    }
  }, [currentWorkflow, dispatch]);

  // Don't render if currentWorkflow is null (during initialization)
  if (!currentWorkflow) {
    return (
      <SettingsContainer>
        <SettingsHeader>
          <SettingsTitle>{I('Workflow Settings')}</SettingsTitle>
          <SettingsSubtitle>
            {I('Initializing workflow settings...')}
          </SettingsSubtitle>
        </SettingsHeader>
      </SettingsContainer>
    );
  }



  return (
    <SettingsContainer>
      <SettingsHeader>
        <SettingsTitle>{I('Workflow Settings')}</SettingsTitle>
        <SettingsSubtitle>
          {I('Configure your workflow execution and debugging settings')}
        </SettingsSubtitle>
      </SettingsHeader>

      <SettingsContent>


        {/* Execution Settings */}
        <SettingsSection>
          <SectionTitle>
            <SectionIcon className="fa fa-play-circle" />
            {I('Execution')}
          </SectionTitle>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="enableTimeout"
                checked={settings.enableTimeout}
                onChange={(e) => handleSettingChange('enableTimeout', e.target.checked)}
              />
              <CheckboxLabel htmlFor="enableTimeout">
                {I('Enable execution timeout')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Stop execution if it runs longer than specified time')}</HelpText>
          </FormGroup>

          {settings.enableTimeout && (
            <FormGroup>
              <FormLabel>{I('Timeout Duration')}</FormLabel>
              <TimeoutInput>
                <TextInputRow
                  type="number"
                  value={settings.timeout}
                  onChange={(e) => handleSettingChange('timeout', parseInt(e.target.value) || 300)}
                  min="1"
                  style={{ width: '120px' }}
                />
                <TimeoutUnit>{I('seconds')}</TimeoutUnit>
              </TimeoutInput>
              <HelpText>{I('Maximum execution time in seconds')}</HelpText>
            </FormGroup>
          )}

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="retryOnFailure"
                checked={settings.retryOnFailure}
                onChange={(e) => handleSettingChange('retryOnFailure', e.target.checked)}
              />
              <CheckboxLabel htmlFor="retryOnFailure">
                {I('Retry on failure')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Automatically retry failed executions')}</HelpText>
          </FormGroup>

          {settings.retryOnFailure && (
            <>
              <FormGroup>
                <FormLabel>{I('Max Retries')}</FormLabel>
                <TextInputRow
                  type="number"
                  value={settings.maxRetries}
                  onChange={(e) => handleSettingChange('maxRetries', parseInt(e.target.value) || 3)}
                  min="1"
                  max="10"
                  style={{ width: '120px' }}
                />
                <HelpText>{I('Maximum number of retry attempts')}</HelpText>
              </FormGroup>

              <FormGroup>
                <FormLabel>{I('Retry Interval')}</FormLabel>
                <TimeoutInput>
                  <TextInputRow
                    type="number"
                    value={settings.retryInterval}
                    onChange={(e) => handleSettingChange('retryInterval', parseInt(e.target.value) || 60)}
                    min="1"
                    style={{ width: '120px' }}
                  />
                  <TimeoutUnit>{I('seconds')}</TimeoutUnit>
                </TimeoutInput>
                <HelpText>{I('Wait time between retry attempts')}</HelpText>
              </FormGroup>
            </>
          )}
        </SettingsSection>

        {/* Data & Logging Settings */}
        <SettingsSection>
          <SectionTitle>
            <SectionIcon className="fa fa-database" />
            {I('Data & Logging')}
          </SectionTitle>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="saveExecutionData"
                checked={settings.saveExecutionData}
                onChange={(e) => handleSettingChange('saveExecutionData', e.target.checked)}
              />
              <CheckboxLabel htmlFor="saveExecutionData">
                {I('Save execution data')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Store execution results and intermediate data')}</HelpText>
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="saveManualExecutions"
                checked={settings.saveManualExecutions}
                onChange={(e) => handleSettingChange('saveManualExecutions', e.target.checked)}
              />
              <CheckboxLabel htmlFor="saveManualExecutions">
                {I('Save manual execution data')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Also save data from manual test executions')}</HelpText>
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="enableDebugMode"
                checked={settings.enableDebugMode}
                onChange={(e) => handleSettingChange('enableDebugMode', e.target.checked)}
              />
              <CheckboxLabel htmlFor="enableDebugMode">
                {I('Enable debug mode')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Show detailed execution logs and debug information')}</HelpText>
          </FormGroup>
        </SettingsSection>

        {/* Development Settings */}
        <SettingsSection>
          <SectionTitle>
            <SectionIcon className="fa fa-cog" />
            {I('Development')}
          </SectionTitle>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="autoSave"
                checked={settings.autoSave}
                onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
              />
              <CheckboxLabel htmlFor="autoSave">
                {I('Auto-save changes')}
              </CheckboxLabel>
            </CheckboxGroup>
            <HelpText>{I('Automatically save workflow changes as you work')}</HelpText>
          </FormGroup>
        </SettingsSection>
      </SettingsContent>

      <SaveButtonContainer>
        <ResetButton
          onClick={handleReset}
          disabled={!hasChanges}
          title={hasChanges ? I('Reset to saved values') : I('No changes to reset')}
        >
          <i className="fa fa-undo" />
          {I('Reset')}
        </ResetButton>
        
        <SaveButton
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          title={hasChanges ? I('Save workflow settings') : I('No changes to save')}
        >
          {isSaving ? (
            <>
              <i className="fa fa-spinner fa-spin" />
              {I('Saving...')}
            </>
          ) : (
            <>
              <i className="fa fa-save" />
              {I('Save Settings')}
            </>
          )}
        </SaveButton>
      </SaveButtonContainer>
    </SettingsContainer>
  );
};

export default WorkflowSettings; 